// © Microsoft Corporation. All rights reserved.

import { IActionPayload } from "@msnews/experiences-redux";
import { MilestoneDataActions, getMilestoneDataConnector, ExperienceMilestoneEventTypes } from "@msnews/milestone-data";
import { registerResetCallback } from "@msnews/core";
import { hasExperiencePersistentDataUpdated } from "./PageVisibilityActionHelpers";
import { logException, markTtprRaf, PrerenderRevalidateException } from "@msnews/diagnostics";

/**
 * Base class for callback information for when an experience may be stale.
 */
interface StaleCallbackBase {
    /** The experience type */
    experienceType: string,

    /** If the page reveal delay is above this, invoke the callback. */
    thresholdMinutes?: number;
}

/**
 * Callback information for when an experience may be stale for React.
 */
export interface StaleCallbackReact extends StaleCallbackBase {
    /** Called when an experience is stale. Returns true if the experience took action to refresh itself. */
    callback: () => boolean;
}

/**
 * Callback information for when an experience may be stale for Web Components.
 */
export interface StaleCallbackWC extends StaleCallbackBase {
    /** Called when an experience is stale. Promise resolves when the refresh is complete. */
    asyncCallback: () => Promise<void>;
}

type StaleCallback = StaleCallbackReact | StaleCallbackWC;

/** List of callbacks to potentially invoke on page reveal. */
let staleCallbacks: StaleCallback[] = [];

/**
 * Register a callback to reset the pageRevealDependencyMap.
 * The callback is registered once when the module is loaded and invoked once on the server side at the beginning of each request.
 */
registerResetCallback(() => {
    staleCallbacks = [];
});

/**
 * Initializes the page reveal tracker.
 */
export function initializePageRevealTracker(): void {
    MilestoneDataActions.updatePageRevealStart.registerObserver((actionPayload: IActionPayload) => {
        const wasPageHidden: boolean = actionPayload.params[1];
        if (!wasPageHidden) {
            // return if page was not in a hidden or prerendered state.
            return;
        }

        const pageRevealStartTimeMs: DOMHighResTimeStamp = actionPayload.params[0];

        const visuallyReadyDependencies: string[] = actionPayload.params[2];

        const milestoneConnector = getMilestoneDataConnector();
        const visuallyReadyEndTime = milestoneConnector.getCurrentState().aboveTheFoldVisuallyReady.endTime;

        const invokedExperiences: Set<string> = new Set<string>();

        for (const staleCallback of staleCallbacks) {
            const { experienceType } = staleCallback;
            const markedAsInvalid: boolean = hasExperiencePersistentDataUpdated(experienceType);
            const stale: boolean = (staleCallback.thresholdMinutes > 0) && (pageRevealStartTimeMs - visuallyReadyEndTime > staleCallback.thresholdMinutes * 60000);
            if (markedAsInvalid || stale) {
                if ("callback" in staleCallback) {
                    // This is a React registration. We can fire and forget: BaseExperience will know when the component updates and mark the reveal as complete.
                    try {
                        // If the callback took no action, mark reveal as complete right away.
                        if (!staleCallback.callback()) {
                            markRevealComplete(experienceType);
                        }
                    } catch (error) {
                        logException(
                            error,
                            PrerenderRevalidateException,
                            "Exception when running stale revalidate callback",
                            "Experience: " + experienceType);
                    }
                } else {
                    // This is a Web Component registration. We'll log completion after the callback is fulfilled.
                    // Do not await the callback, each will be invoked in parallel.
                    invokeWebComponentStaleCallback(staleCallback);
                }
            } else {
                // If the callback doesn't need to be invoked, mark reveal complete.
                markRevealComplete(experienceType);
            }

            invokedExperiences.add(experienceType);
        }

        // Experiences that are required for visually ready should have reveal marked complete at some point, even if they are not stale liable
        // This is required for ExperienceTracker to declare the whole page as revealed.
        for (const visuallyReadyDependency of visuallyReadyDependencies) {
            if (!invokedExperiences.has(visuallyReadyDependency)) {
                markRevealComplete(visuallyReadyDependency);
            }
        }
    });
}

/**
 * Invoke the stale callback for the Web Component experience.
 * @param staleCallbackWC The callback to invoke.
 */
async function invokeWebComponentStaleCallback(staleCallbackWC: StaleCallbackWC) {
    try {
        await staleCallbackWC.asyncCallback();
        markRevealComplete(staleCallbackWC.experienceType);
    } catch (error) {
        logException(
            error,
            PrerenderRevalidateException,
            "Exception when running stale revalidate callback",
            "Experience: " + staleCallbackWC.experienceType);
    }
}

/**
 * Mark the page reveal as complete for the given experience type.
 * @param experienceType The experience type to mark as complete.
 */
function markRevealComplete(experienceType: string) {
    markTtprRaf(experienceType);
    MilestoneDataActions.updateExperiencePublicState.getActionSender(getMilestoneDataConnector()).send({
        eventType: ExperienceMilestoneEventTypes.staleRevalidationComplete,
        experienceType
    });
}

/**
 * Registers a callback to run and update stale data when appropriate. This registers the callback for the React rendering mode, where BaseExperience will take care of the post-refresh reporting.
 * @param staleCallback Information about the callback to register.
 */
export function registerStaleRevealReact(staleCallback: StaleCallbackReact) {
    staleCallbacks.push(staleCallback);
}

/**
 * Registers a callback to run and update stale data when appropriate. This registers the callback for the Web Component rendering mode, and will automatically log completion when the
 * callback promise is fulfilled.
 * @param staleCallback Information about the callback to register.
 */
export function registerStaleRevealWC(staleCallback: StaleCallbackWC) {
    staleCallbacks.push(staleCallback);
}